import React from "react";
import { Box, Skeleton, Tooltip, Typography } from "@mui/material";
import { useMetricsUi, SectionHead } from "../../components/metrics/metricsUi";
import { MetricLabel } from "./metricDefinitions";

// How many lines a StatCard's value shows before it's clipped with an
// ellipsis — the full value is still always available via hover (Tooltip).
const VALUE_LINE_CLAMP = 2;

// Shared visual primitives for Planning > Forecast, built on the same
// design tokens as the Sales Performance page (components/metrics/metricsUi).

/** Muted uppercase label + tabular value, in a bordered card. */
export const StatCard = ({ label, value, hint, info }) => {
  const ui = useMetricsUi();
  return (
    <Box
      sx={{
        ...ui.card,
        p: "6px 8px",
        minHeight: 42,
        display: "flex",
        flexDirection: "column",
        gap: 0.15,
        transition: "border-color .12s",
        "&:hover": { borderColor: ui.borderStrong },
      }}
    >
      <Typography
        sx={{
          fontSize: 8.5,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: ui.colors.textSecondary,
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        // With a definition the hover shows that instead of the bare label.
        title={!info && typeof label === "string" ? label : undefined}
      >
        {info ? (
          <MetricLabel metric={info} iconSize={10}>
            {label}
          </MetricLabel>
        ) : (
          label
        )}
      </Typography>
      <Tooltip
        title={
          value === null || value === undefined || value === "" ? "" : value
        }
        placement="top"
        arrow
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 400,
            letterSpacing: "-0.01em",
            fontVariantNumeric: "tabular-nums",
            color: ui.colors.textPrimary,
            lineHeight: 1.25,
            wordBreak: "break-word",
            display: "-webkit-box",
            WebkitLineClamp: VALUE_LINE_CLAMP,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value === null || value === undefined || value === "" ? "—" : value}
        </Typography>
      </Tooltip>
      {hint && (
        <Typography sx={{ fontSize: 8.5, color: ui.colors.textTertiary }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
};

// Tracks sized down to fit more cards per row; still wide enough that long
// labels ("Recommended Order Qty", "Avg Daily Demand") stay on one line.
export const cardGridSx = {
  display: "grid",
  gap: 0.75,
  gridTemplateColumns: "repeat(auto-fill, minmax(136px, 1fr))",
};

/** Loading placeholder that mirrors a StatCard. */
export const StatCardSkeleton = () => {
  const ui = useMetricsUi();
  return (
    <Box
      sx={{
        ...ui.card,
        p: "6px 8px",
        minHeight: 42,
        display: "flex",
        flexDirection: "column",
        gap: 0.4,
      }}
    >
      <Skeleton variant="text" width="55%" height={8} animation="wave" />
      <Skeleton variant="text" width="72%" height={14} animation="wave" />
    </Box>
  );
};

// Uneven heights so the chart placeholder reads as a bar chart's silhouette,
// not a flat block.
const BAR_SKELETON_HEIGHTS = [55, 78, 45, 90, 62, 80, 50, 70, 85, 58, 72];

/**
 * Full result-area placeholder shown in the spot where the metrics + chart
 * render — while a forecast run is in flight, or while a different
 * SKU/product's history is loading. `message` sits under the chart; pass ""
 * to omit it (e.g. when just switching history selection, not running one).
 */
export const ForecastResultSkeleton = ({
  message = "Generating forecast… this can take a moment.",
} = {}) => {
  const ui = useMetricsUi();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <SectionHead title="Forecast Summary" />
        <Box sx={cardGridSx}>
          {Array.from({ length: 8 }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </Box>
      </Box>

      <Box>
        <SectionHead title="Forecasted Units" />
        <Box sx={{ ...ui.card, p: "16px 12px 8px" }}>
          <Box
            sx={{
              height: 260,
              display: "flex",
              alignItems: "flex-end",
              gap: 1.25,
              px: 1,
            }}
          >
            {BAR_SKELETON_HEIGHTS.map((h, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                animation="wave"
                sx={{
                  flex: 1,
                  height: `${h}%`,
                  borderRadius: "4px 4px 0 0",
                  transform: "none",
                }}
              />
            ))}
          </Box>
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="text" height={22} animation="wave" />
            ))}
          </Box>
        </Box>
      </Box>

      {message && (
        <Typography sx={{ fontSize: 12, color: ui.colors.textSecondary }}>
          {message}
        </Typography>
      )}
    </Box>
  );
};
